import { UI } from './ui';
import { Message } from './types';

export class App {
  private ui: UI;
  private messages: Message[] = [];
  private chatEndpoint: string;
  private mcpEndpoint: string;
  private sessionId: string;

  constructor() {
    this.sessionId = (crypto && 'randomUUID' in crypto) ? crypto.randomUUID() : String(Date.now());
    this.ui = new UI(this.sessionId);
    this.chatEndpoint = import.meta.env.VITE_CHAT_API_URL || '/chat';
    this.mcpEndpoint = import.meta.env.VITE_MCP_SERVER_URL || '/mcp';

    // Set up context update handler for MCP app's updateModelContext()
    this.ui.setOnContextUpdate((context) => {
      this.handleContextUpdate(context);
    });
  }

  async init() {
    // Show welcome message
    // this.ui.addMessage({
    //   role: 'system',
    //   content: '✅ Verbunden! Unterstützt durch GPT-5.2. Sie können mit mir chatten oder mich bitten, einen Raum zu buchen.',
    //   timestamp: Date.now(),
    // });

    this.ui.addMessage({
      role: 'assistant',
      content: "Hallo! Ich bin hier, um Ihnen bei der Buchung eines Raums in unserem Coworking-Space zu helfen. Sagen Sie mir einfach, was Sie brauchen!",
      timestamp: Date.now(),
    });

    // Set up event listeners
    this.ui.onSendMessage(() => this.handleSendMessage());
    this.ui.focusInput();
  }

  private handleContextUpdate(context: any) {
    console.log('[App] Context updated from MCP app:', context);
  }


  private async parseResponse(response: Response): Promise<any> {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('text/event-stream')) {
      // Handle SSE response
      const text = await response.text();
      // Parse SSE format - extract JSON from data: lines
      const lines = text.split('\n').filter(line => line.startsWith('data: '));
      if (lines.length === 0) {
        throw new Error('No data in SSE response');
      }
      const jsonData = JSON.parse(lines[lines.length - 1].substring(6));
      return jsonData;
    } else {
      // Handle JSON response
      return await response.json();
    }
  }

  private async handleSendMessage() {
    const userMessage = this.ui.getInputValue();

    if (!userMessage) return;

    // Add user message to UI and conversation history
    this.ui.addMessage({
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    });

    this.messages.push({
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    });

    // Clear input and disable during processing
    this.ui.clearInput();
    this.ui.setInputEnabled(false);
    this.ui.showTypingIndicator();

    try {
      // Call backend chat endpoint
      const chatResponse = await fetch(this.chatEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          messages: this.messages
            .filter(m => m.role !== 'system')
            .map(m => ({
              role: m.role,
              content: m.content,
            })),
        }),
      });

      if (!chatResponse.ok) {
        throw new Error(`Chat API error: ${chatResponse.statusText}`);
      }

      const response = await chatResponse.json();

      this.ui.hideTypingIndicator();

      // Add assistant message
      this.ui.addMessage({
        role: 'assistant',
        content: response.message,
        timestamp: Date.now(),
      });

      this.messages.push({
        role: 'assistant',
        content: response.message,
        timestamp: Date.now(),
      });

      // Check if we should show the booking UI
      if (response.shouldCallTool) {
        await this.showBookingUI(response.toolArgs);
      }
    } catch (error) {
      this.ui.hideTypingIndicator();
      this.ui.showError(
        error instanceof Error ? error.message : 'Antwort konnte nicht empfangen werden'
      );
    } finally {
      this.ui.setInputEnabled(true);
      this.ui.focusInput();
    }
  }

  private async showBookingUI(toolArgs?: Record<string, any>) {
    try {
      // Step 1: Call the book-room tool to get initial data
      const toolResponse = await fetch(this.mcpEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, text/event-stream',
          'X-Session-ID': this.sessionId,
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'tools/call',
          params: {
            name: 'book-room',
            arguments: toolArgs || {},
          },
        }),
      });

      const toolData = await this.parseResponse(toolResponse);
      if (toolData.error) {
        throw new Error(toolData.error.message);
      }

      // Step 2: Fetch the booking UI HTML
      const uiResponse = await fetch(this.mcpEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, text/event-stream',
          'X-Session-ID': this.sessionId,
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'resources/read',
          params: {
            uri: 'ui://room-booking/main',
          },
        }),
      });

      const uiData = await this.parseResponse(uiResponse);
      if (uiData.error) {
        throw new Error(uiData.error.message);
      }

      const htmlContent = uiData.result.contents[0].text;

      // Step 3: Add the booking UI inline with tool result data
      this.ui.addMessage({
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        appHtml: htmlContent,
        toolResult: toolData.result, // Pass tool result to UI
      });
    } catch (error) {
      this.ui.showError(
        `Buchungsoberfläche konnte nicht geladen werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
      );
    }
  }
}
